import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Check } from "lucide-react";
import { StepCustomer } from "../components/register/StepCustomer";
import { StepItem } from "../components/register/StepItem";
import { StepRoute } from "../components/register/StepRoute";
import { StepReview } from "../components/register/StepReview";
import { RegisterSuccess } from "../components/register/RegisterSuccess";
import { initialForm, validateStep, toPayload } from "../components/register/wizard";
import { api, errorField, errorMessage } from "../lib/api";

const STEPS = ["Customer", "Vehicle / Item", "Pickup & Delivery", "Review"];
const FIELD_STEP = { customer: 0, item: 1, pickup: 2, delivery: 2, pickup_date: 2 };

const Progress = ({ step }) => (
  <ol className="grid grid-cols-4 gap-2" data-testid="wizard-progress">
    {STEPS.map((s, i) => (
      <li key={s} data-testid={`wizard-progress-${i}`} data-active={i === step}>
        <div className={`h-1 ${i <= step ? "bg-amber-500" : "bg-slate-200"} transition-colors`} />
        <div className={`mt-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${i === step ? "text-brand-600" : i < step ? "text-slate-700" : "text-slate-500"}`}>
          {i < step ? <Check className="w-3 h-3" /> : <span>{String(i + 1).padStart(2, "0")}</span>}<span className="hidden sm:inline">{s}</span>
        </div>
      </li>
    ))}
  </ol>
);

export default function Register() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(() => initialForm(params));
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const set = (section, key) => (v) => {
    setForm((f) => (section ? { ...f, [section]: { ...f[section], [key]: v } } : { ...f, [key]: v }));
    setErrors((e) => ({ ...e, [section ? `${section}.${key}` : key]: undefined }));
  };
  const top = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const next = () => {
    const errs = validateStep(step, form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStep((s) => s + 1); top();
  };
  const back = () => { setSubmitError(""); setStep((s) => s - 1); top(); };

  const submit = async () => {
    setSubmitting(true); setSubmitError("");
    try {
      const { data } = await api.post("/shipments", toPayload(form));
      setResult(data); top();
    } catch (e) {
      const field = errorField(e);
      const msg = errorMessage(e);
      setSubmitError(msg);
      if (field) { setErrors({ [field]: msg }); setStep(FIELD_STEP[field.split(".")[0]] ?? 3); }
    } finally {
      setSubmitting(false);
    }
  };

  const Body = [StepCustomer, StepItem, StepRoute, StepReview][step];
  return (
    <section className="relative" data-testid="register-page">
      <div className="absolute inset-x-0 top-0 h-72 grid-lines bg-[#F7F8FA] border-b border-slate-200" />
      <div className="relative container-x max-w-5xl py-14 md:py-20">
        {result ? <RegisterSuccess result={result} /> : (
          <>
            <span className="eyebrow">Register shipment</span>
            <h1 className="mt-4 font-display font-black uppercase text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[0.92]">Book your transport</h1>
            <p className="mt-4 text-slate-600 max-w-xl">Three short steps. You get a tracking number instantly. No upfront payment.</p>
            <div className="mt-10"><Progress step={step} /></div>
            <div className="mt-8 panel p-5 sm:p-8">
              <h2 className="font-display font-bold uppercase text-slate-900 text-2xl mb-6" data-testid="wizard-step-title">{STEPS[step]}</h2>
              <Body form={form} set={set} errors={errors} />
              {submitError && <p className="mt-6 text-sm text-red-600 border border-red-500/40 bg-red-500/10 px-4 py-3" data-testid="wizard-submit-error">{submitError}</p>}
              <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-between gap-3">
                <button type="button" onClick={back} disabled={step === 0} className="btn-ghost disabled:opacity-30 disabled:pointer-events-none" data-testid="wizard-step-back-button"><ArrowLeft className="w-4 h-4" />Back</button>
                {step < 3 ? (
                  <button type="button" onClick={next} className="btn-amber" data-testid="wizard-step-next-button">Continue <ArrowRight className="w-4 h-4" /></button>
                ) : (
                  <button type="button" onClick={submit} disabled={submitting} className="btn-amber" data-testid="wizard-submit-button">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit shipment <ArrowRight className="w-4 h-4" /></>}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
